import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, []);

  const onTouchStart = (event: ReactTouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} - 佐山爱影视作品`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 via-primary-950/45 to-primary-950/80" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center justify-center px-5 md:px-10">
        <div className="w-full max-w-3xl text-center">
          {heroSlides.map((slide, i) => (
            <div
              key={slide.id}
              className={`transition-all duration-700 ${
                i === index ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 absolute inset-x-0 top-1/2 -translate-y-1/2 px-5 md:px-10 pointer-events-none"
              }`}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-accent-500 text-primary-950 mb-5">
                <i className="ri-fire-line" />
                {slide.tag}
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-white leading-tight mb-4">
                《{slide.title}》
              </h2>
              <p className="text-sm md:text-base text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                {slide.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to={`/works/${slide.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-accent-500 text-primary-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看作品详情
                </Link>
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-white/10 text-white border border-white/25 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-grid-line text-lg" />
                  浏览全部作品
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white border border-white/25 hover:bg-white/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white border border-white/25 hover:bg-white/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}